/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable react/no-array-index-key */
/* eslint-disable react/jsx-closing-bracket-location */

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { Box } from '../LayoutPrimitives';
import Button from '../Button';
import { IconChevronLeft, IconChevronRight } from '../icons/Icons';

import { useSwipeDetection } from '../../../hooks/useSwipeDetection';
import announce from '../../../helpers/announce';
import { STATUS } from '../../../constants';

import FauxImage, { FauxUnavailableImage } from '../FauxImage';
import DotPagination from './DotPagination';
import MediaGalleryModal from '../MediaGalleryModal';

const { IDLE, PENDING, SUCCESS, ERROR } = STATUS;

export default function SlideShow({
    isLoading,
    images = [],
    aspectRatio = '4:3',
    status,
    ...rest
}) {
    const [slideImages, setSlideImages] = useState();
    const [activeIndex, setActiveIndex] = useState(0);
    const [isModalOpen, setModalOpen] = useState(false);
    const [areControlsVisible, setControlsVisible] = useState(false);
    const [controlsClass, setControlsClass] = useState();
    const outerContainerRef = useRef();
    const containerRef = useRef();
    const isFirstRender = useRef(true);

    useEffect(() => {
        let timeout;

        if (areControlsVisible) {
            clearTimeout(timeout);
            setControlsClass(null);
        } else {
            clearTimeout(timeout);

            timeout = setTimeout(() => {
                setControlsClass('hidden');
            }, 250);
        }

        return () => clearTimeout(timeout);
    }, [areControlsVisible]);

    useEffect(() => {
        let isMounted = true;

        if (isMounted && Array.isArray(images) && !isEmpty(images)) {
            setSlideImages([images[images.length - 1], images[0], images[1]]);
            isFirstRender.current = false;
        }

        return () => {
            isMounted = false;
        };
    }, [images]);

    useEffect(() => {
        let timeout;
        let isMounted = true;

        if (!isFirstRender.current && containerRef.current?.style) {
            clearTimeout(timeout);

            timeout = setTimeout(() => {
                const prevImage =
                    activeIndex === 0
                        ? images[images.length - 1]
                        : images[activeIndex - 1];
                const nextImage =
                    activeIndex === images.length - 1
                        ? images[0]
                        : images[activeIndex + 1];

                if (isMounted) {
                    setSlideImages([prevImage, images[activeIndex], nextImage]);
                    setActiveIndex(activeIndex);
                    containerRef.current.style.transition = 'none';
                    containerRef.current.style.transform = 'translateX(-100%)';
                    announce(
                        `Photo ${activeIndex + 1} of ${images.length}. ${
                            images[activeIndex].altText
                        }`,
                        'polite',
                        'slide-show-announce'
                    );
                }
            }, 200);
        }

        return () => {
            clearTimeout(timeout);
            isMounted = false;
        };
    }, [activeIndex, images]);

    const onNextImage = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (images?.length > 1) {
            containerRef.current.style.transition = 'transform 175ms';
            containerRef.current.style.transform = 'translateX(-200%)';
            const newActiveIndex =
                activeIndex === images.length - 1 ? 0 : activeIndex + 1;
            setActiveIndex(newActiveIndex);
        }
    };

    const onPrevImage = (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }

        if (images?.length > 1) {
            containerRef.current.style.transition = 'transform 175ms';
            containerRef.current.style.transform = 'translateX(0)';
            const newActiveIndex =
                activeIndex === 0 ? images.length - 1 : activeIndex - 1;
            setActiveIndex(newActiveIndex);
        }
    };

    // Change pages when user swipes left or right
    useSwipeDetection(containerRef, {
        right: onPrevImage,
        left: onNextImage,
    });

    const onNextImageKeydown = (e) => {
        if (e.key === 'Enter') {
            onNextImage(e);
        }
    };

    const onPrevImageKeydown = (e) => {
        if (e.key === 'Enter') {
            onNextImage(e);
        }
    };

    const handleKeyDown = (e) => {
        const key = e?.key;

        // eslint-disable-next-line default-case
        switch (key) {
            case 'ArrowRight':
                onNextImage(e);
                break;
            case 'ArrowLeft':
                onPrevImage(e);
                break;
            case 'Enter':
                setModalOpen(true);
                break;
        }
    };

    const maybeSetControlsVisible = (value) => {
        if (value === true) {
            setControlsVisible(true);
        } else {
            const currentFocusedEl = document.activeElement;
            if (!outerContainerRef.current.contains(currentFocusedEl)) {
                setControlsVisible(false);
            }
        }
    };

    if (isLoading || status === PENDING || status === IDLE) {
        return (
            <FauxImage isLoading aspectRatio={aspectRatio} altText="Loading" {...rest} />
        );
    }

    if (status === ERROR || isEmpty(images)) {
        return <FauxUnavailableImage aspectRatio={aspectRatio} {...rest} />;
    }

    if (images.length === 1) {
        return (
            <FauxImage
                aspectRatio={aspectRatio}
                src={images[0].url}
                altText={images[0].altText}
                {...rest}
            />
        );
    }

    return (
        <div
            data-component="SlideShow"
            ref={outerContainerRef}
            onKeyDown={handleKeyDown}
            onMouseEnter={() => maybeSetControlsVisible(true)}
            onMouseLeave={() => maybeSetControlsVisible(false)}
            onFocus={() => maybeSetControlsVisible(true)}
            onBlur={() => maybeSetControlsVisible(false)}
            {...rest}
        >
            <span className="rec-sr-only" id="slide-instructions">
                This is a carousel of images. Use left and right arrows to navigate. Click
                to open full image gallery.
            </span>
            <div
                role="button"
                data-sarsa-slides-faux-button
                tabIndex="0"
                onClick={() => setModalOpen(true)}
                aria-describedby="slide-instructions"
            >
                <div data-sarsa-slides-container ref={containerRef}>
                    {!!slideImages &&
                        !isEmpty(slideImages) &&
                        slideImages.map((item, index) => (
                            <FauxImage
                                aria-hidden={index !== 1}
                                key={images.length >= 3 ? item.url : index}
                                aspectRatio={aspectRatio}
                                src={item.url}
                                altText={item.altText}
                            />
                        ))}
                </div>
            </div>

            <Box
                padding="xs"
                alignX="space-between"
                className={cx('control-buttons', controlsClass)}
            >
                <Button
                    onClick={onPrevImage}
                    onKeyDown={onPrevImageKeydown}
                    appearance="icon-circular"
                    size="sm"
                    iconBeforeElement={<IconChevronLeft />}
                    aria-label="Previous image"
                />
                <Button
                    onClick={onNextImage}
                    onKeyDown={onNextImageKeydown}
                    appearance="icon-circular"
                    size="sm"
                    iconBeforeElement={<IconChevronRight />}
                    aria-label="Next image"
                />
            </Box>
            <DotPagination
                activeIndex={activeIndex}
                itemCount={images.length}
                onClick={setActiveIndex}
            />
            <MediaGalleryModal
                images={images}
                isOpen={isModalOpen}
                onRequestClose={() => setModalOpen(false)}
                activeIndex={activeIndex}
            />
        </div>
    );
}

SlideShow.propTypes = {
    isLoading: PropTypes.bool,

    /** API fetch status */
    status: PropTypes.oneOf([IDLE, PENDING, ERROR, SUCCESS]),

    /**
     * Images in the slide show will always conform to ths aspect ratio,
     * zooming/cropping to fit as needed. THis value MUST be provided as a string
     * with a width ratio followed by a colon followed by the height ratio,
     * such as ”16:9”
     */
    aspectRatio: PropTypes.string,

    /**
     * Array of objects representing the images to display. This array is also
     * passed directly to the MediaGalleryModal
     */
    images: PropTypes.arrayOf(
        PropTypes.shape({
            url: PropTypes.string,
            altText: PropTypes.string,
        })
    ),
};
